/**
 * AI Recruit - Resume Formatter Templates - Document Map Editor
 *
 * Rich WYSIWYG review/rebind screen where an admin reviews an AI-generated
 * resume template before publishing it. Ported from VMP's
 * document-map-editor.component.ts (Angular) - same interaction model
 * (contenteditable document + click-to-rebind popover + right-click
 * insert-variable menu + JSON-tree drag-and-drop + document.execCommand
 * rich-text toolbar), rebuilt in React since none of the original logic is
 * Angular-specific (it operates directly on the DOM/Selection/Range APIs).
 *
 * ROUND-TRIP CONTRACT (see backend/utils/resume_template_engine/field_schema.py
 * and template_html_annotator.py): the document always DISPLAYS the literal
 * "{{field}}" tag for a bound span - never a resolved example value - and
 * "data-repeat" elements show their real item count computed by walking
 * example_analysis_json. Before saving, the DOM is cloned, editor-only
 * attributes (data-node-id, contenteditable, data-repeat-count) are
 * stripped, and every [data-bind] span's text is (re)written to its literal
 * "{{path}}" form - the backend's Mustache renderer works off that literal
 * text, not the data-bind attribute itself.
 *
 * Simplifications vs the original (see final report for the full list):
 * format painter, the change-case dropdown, cut/copy toolbar buttons, and
 * the floating hover-to-delete-table "x" button were dropped - a table can
 * still be fully deleted/unwrapped via the right-click menu when the cursor
 * is inside it. The original's split save-button dropdown is instead two
 * plain "Save as Active" / "Save as Draft" buttons per this port's own spec.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  QuestionMarkCircleIcon,
  TableCellsIcon,
  ChevronDownIcon,
  ListBulletIcon,
  BoldIcon,
  ItalicIcon,
  CodeBracketIcon,
  InformationCircleIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3CenterLeftIcon,
  Bars3Icon,
  PencilSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  fetchDocumentMap,
  saveDocumentMap,
  previewDocumentMap,
  testDocumentMapWithResume,
  type ResumeFormatTemplate,
} from '@/services/resumeTemplateApi'
import JsonTreeNode, { type JsonTreeDragPayload, type JsonValue } from './JsonTreeNode'

interface DocumentMapEditorProps {
  templateId: string
  onClose: () => void
}

interface VariableOption {
  path: string
  label: string
}

const FONT_SIZE_STEPS_PT = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 72]

// ─── Pure JSON-shape helpers (ported from the Angular component's private
// methods - plain data-walking logic, nothing DOM/framework-specific) ───

const isPlainObject = (value: JsonValue): value is { [key: string]: JsonValue } =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

// Recursively flattens an object's own SCALAR leaves into dotted paths - the
// single source of truth for "what fields can I bind here", derived live
// from the actual resume_analysis JSON rather than any hardcoded list.
// Arrays are skipped (handled via repeating sections, not scalar binds).
function flattenScalarFields(obj: JsonValue, prefix = ''): VariableOption[] {
  const results: VariableOption[] = []
  if (!isPlainObject(obj)) return results
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    const path = prefix ? `${prefix}.${key}` : key
    if (value === null || typeof value !== 'object') {
      results.push({ path, label: path })
    } else if (!Array.isArray(value)) {
      results.push(...flattenScalarFields(value, path))
    }
  }
  return results
}

// Recursively finds every ARRAY-valued field (bare name if inside a
// repeating item's shape, otherwise a full dotted path) - the options
// offered by the toolbar's "Repeat" button.
function flattenArrayFields(obj: JsonValue, prefix = ''): VariableOption[] {
  const results: VariableOption[] = []
  if (!isPlainObject(obj)) return results
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    const path = prefix ? `${prefix}.${key}` : key
    if (Array.isArray(value)) {
      results.push({ path, label: path })
    } else if (value !== null && typeof value === 'object') {
      results.push(...flattenArrayFields(value, path))
    }
  }
  return results
}

// Walks from the outermost enclosing [data-repeat] down to `groupEl`,
// drilling into the real example_analysis_json's array items at each level -
// except the FINAL segment when `keepFinalArray` is true, which returns the
// array itself rather than its first item (used to report a real item count
// rather than resolve a single representative item/value).
function walkRepeatChain(groupEl: HTMLElement, keepFinalArray: boolean, root: JsonValue): JsonValue {
  const chain: string[] = []
  let current: HTMLElement | null = groupEl
  while (current) {
    const path = current.getAttribute('data-repeat')
    if (path) chain.unshift(path)
    const parent: HTMLElement | null = current.parentElement
    current = parent ? (parent.closest('[data-repeat]') as HTMLElement | null) : null
  }

  let node: JsonValue = root
  for (let i = 0; i < chain.length; i++) {
    const segment = chain[i]
    if (node === null || node === undefined) return null
    const parts = node === root ? segment.split('.') : [segment]
    for (const part of parts) {
      if (!isPlainObject(node)) return null
      node = node[part] ?? null
    }
    const isLast = i === chain.length - 1
    if (Array.isArray(node) && (!isLast || !keepFinalArray)) {
      node = node.length ? node[0] : null
    }
  }
  return node
}

function resolveRepeatArrayLength(groupEl: HTMLElement, root: JsonValue): number | null {
  const arr = walkRepeatChain(groupEl, true, root)
  return Array.isArray(arr) ? arr.length : null
}

// The resolved first item's shape, restricted to real OBJECT items - used
// for field-name validation/suggestion.
function resolveRepeatItemShape(groupEl: HTMLElement, root: JsonValue): Record<string, JsonValue> | null {
  const item = walkRepeatChain(groupEl, false, root)
  return isPlainObject(item) ? item : null
}

// Only fields that genuinely exist on the resolved repeating item's shape
// (or any top-level path, when not inside a repeat at all) may be bound -
// this is what stops a typo'd/invented field name from being added.
function isValidFieldForContext(fieldPath: string, groupEl: HTMLElement | null, root: JsonValue): boolean {
  if (fieldPath === '.') return true
  if (!groupEl) return true
  const shape = resolveRepeatItemShape(groupEl, root)
  if (!shape) return true // can't determine the real shape - fail open rather than block legitimate use
  return flattenScalarFields(shape).some((f) => f.path === fieldPath)
}

function resolveArrayByPath(path: string, groupEl: HTMLElement | null, root: JsonValue): JsonValue[] | null {
  let node: JsonValue
  if (groupEl) {
    const shape = resolveRepeatItemShape(groupEl, root)
    node = shape ? (shape[path] ?? null) : null
  } else {
    node = root
    for (const part of path.split('.')) {
      if (!isPlainObject(node)) return null
      node = node[part] ?? null
    }
  }
  return Array.isArray(node) ? node : null
}

// Any field ALREADY bound somewhere else within this same repeating group -
// used as a fallback suggestion when the JSON shape can't be resolved (e.g.
// no example JSON stored for this template yet).
function discoveredSubFieldOptions(groupEl: HTMLElement): VariableOption[] {
  const seen = new Set<string>()
  const options: VariableOption[] = []
  groupEl.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => {
    if (el.closest('[data-repeat]') !== groupEl) return // belongs to a nested sub-group instead
    const path = el.getAttribute('data-bind')
    if (!path || path === '.' || seen.has(path)) return
    seen.add(path)
    options.push({ path, label: path })
  })
  return options
}

function closestGroupForNode(node: Node | null): HTMLElement | null {
  const el = node ? (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement) : null
  return el ? (el.closest('[data-repeat]') as HTMLElement | null) : null
}

const caretRangeFromPoint = (x: number, y: number): Range | null => {
  if (typeof document.caretRangeFromPoint === 'function') {
    return document.caretRangeFromPoint(x, y)
  }
  if (typeof document.caretPositionFromPoint === 'function') {
    const pos = document.caretPositionFromPoint(x, y)
    if (!pos) return null
    const range = document.createRange()
    range.setStart(pos.offsetNode, pos.offset)
    range.collapse(true)
    return range
  }
  return null
}

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: string } } }).response
    if (response?.data?.detail) return response.data.detail
  }
  return fallback
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// Descendant-selector styling for the raw, editor-injected HTML inside the
// contenteditable document - ported from the original's ::ng-deep rules
// (React has no view-encapsulation to escape, so these are plain selectors
// scoped by the .dme-doc wrapper class instead).
const DOC_STYLES = `
.dme-doc table { border-collapse: collapse; width: 100%; margin: 6px 0; }
.dme-doc table td, .dme-doc table th { border: 1px solid #adb5bd; padding: 4px 8px; vertical-align: top; }
.dme-doc [data-bind] { background-color: #eafaf1; outline: 1px dashed #34c38f; outline-offset: 1px; cursor: pointer; border-radius: 3px; }
.dme-doc [data-bind]:hover { background-color: #d7f5e6; }
.dme-doc div[data-repeat] { display: block; border: 1px dashed #556ee6; border-radius: 6px; padding: 8px; margin: 8px 0; position: relative; }
.dme-doc div[data-repeat]::before { content: "Repeats \\00d7" attr(data-repeat-count); position: absolute; top: -10px; left: 8px; background: #fff; padding: 0 6px; font-size: 0.7rem; color: #556ee6; font-weight: 600; }
.dme-doc div[data-repeat]:not([data-repeat-count])::before { content: "Repeats"; }
.dme-doc li[data-repeat] { border-bottom: 1px dashed #556ee6; position: relative; }
.dme-doc li[data-repeat]::after { content: "\\00d7" attr(data-repeat-count); margin-left: 6px; font-size: 0.7rem; color: #556ee6; font-weight: 600; }
.dme-doc li[data-repeat]:not([data-repeat-count])::after { content: ""; }
.dme-doc span[data-repeat] { border-bottom: 1px dashed #556ee6; }
.dme-doc span[data-repeat]::after { content: "\\00d7" attr(data-repeat-count); margin-left: 2px; font-size: 0.7rem; color: #556ee6; font-weight: 600; vertical-align: super; }
.dme-doc span[data-repeat]:not([data-repeat-count])::after { content: ""; }
.dme-doc [data-section="header"], .dme-doc [data-section="footer"] { display: block; background-color: #f8f9fa; border-radius: 6px; padding: 8px; margin: 8px 0; font-size: 0.9em; }
.dme-docx-preview .docx-wrapper { background: #fff; padding: 16px 0; }
.dme-docx-preview .docx-page { margin: 0 auto 16px; box-shadow: 0 0 6px rgba(0,0,0,0.15); }
`

const ToolbarButton: React.FC<{
  title: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  active?: boolean
  wide?: boolean
  children: React.ReactNode
}> = ({ title, onClick, active, wide, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`flex h-7 items-center justify-center rounded text-sm text-gray-700 hover:bg-indigo-50 ${
      wide ? 'w-auto gap-1 px-2 whitespace-nowrap' : 'w-7'
    } ${active ? '!bg-indigo-600 !text-white' : ''}`}
  >
    {children}
  </button>
)

const DocumentMapEditor: React.FC<DocumentMapEditorProps> = ({ templateId, onClose }) => {
  const queryClient = useQueryClient()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const splitContainerRef = useRef<HTMLDivElement | null>(null)
  const docxPreviewContainerRef = useRef<HTMLDivElement | null>(null)
  const savedInsertRangeRef = useRef<Range | null>(null)
  const savedGroupElRef = useRef<HTMLElement | null>(null)
  const savedTableRef = useRef<HTMLTableElement | null>(null)
  const menuCellRef = useRef<HTMLTableCellElement | null>(null)
  const resizingRef = useRef(false)
  // True for the duration of a native edit (beforeinput through a short
  // idle window after the matching input event) plus any selectionchange
  // it triggers. Confirmed by direct testing: the font-size-display
  // selectionchange handler below - even doing nothing but a read-only
  // getComputedStyle - still occasionally landed between two keystrokes
  // closely enough to disrupt the caret restoreSelectionOffsets had just
  // placed, scrambling typed character order. Suppressing that handler for
  // this window removes the race without touching its own behavior once
  // typing has actually paused (clicks/arrow-key selection changes still
  // update the toolbar normally).
  const suppressSelectionChangeRef = useRef(false)
  const suppressSelectionChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [isDraft, setIsDraft] = useState(true)
  const [exampleAnalysisJson, setExampleAnalysisJson] = useState<JsonValue>(null)
  const [initialHtml, setInitialHtml] = useState<string | null>(null)
  const [docVersion, setDocVersion] = useState(0)

  const [coveragePanelExpanded, setCoveragePanelExpanded] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  const [previewPaneOpen, setPreviewPaneOpen] = useState(false)
  const [leftPaneWidthPercent, setLeftPaneWidthPercent] = useState(62)
  const [fontSizeDisplayPt, setFontSizeDisplayPt] = useState(11)
  const [previewing, setPreviewing] = useState(false)
  const [previewRendered, setPreviewRendered] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Popover state for rebinding/unbinding an EXISTING bound span.
  const [activeNodeElement, setActiveNodeElement] = useState<HTMLElement | null>(null)
  const [activeNodeGroupElement, setActiveNodeGroupElement] = useState<HTMLElement | null>(null)
  const [activeNodeCurrentBind, setActiveNodeCurrentBind] = useState<string | null>(null)
  const [staticTextDraft, setStaticTextDraft] = useState('')
  const [customFieldPathDraft, setCustomFieldPathDraft] = useState('')
  const [popoverX, setPopoverX] = useState(0)
  const [popoverY, setPopoverY] = useState(0)

  // Right-click "quick format + insert variable" menu.
  const [variableMenuOpen, setVariableMenuOpen] = useState(false)
  const [variableMenuX, setVariableMenuX] = useState(0)
  const [variableMenuY, setVariableMenuY] = useState(0)
  const [variableMenuSearch, setVariableMenuSearch] = useState('')

  // Toolbar "Repeat" button menu.
  const [repeatMenuOpen, setRepeatMenuOpen] = useState(false)
  const [repeatMenuX, setRepeatMenuX] = useState(0)
  const [repeatMenuY, setRepeatMenuY] = useState(0)

  // Toolbar "Table" dropdown menu.
  const [tableMenuOpen, setTableMenuOpen] = useState(false)
  const [tableMenuX, setTableMenuX] = useState(0)
  const [tableMenuY, setTableMenuY] = useState(0)
  const [tableMenuHasCellContext, setTableMenuHasCellContext] = useState(false)

  const [testJsonModalOpen, setTestJsonModalOpen] = useState(false)
  const [testJsonDraft, setTestJsonDraft] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['resume-template-document-map', templateId],
    queryFn: () => fetchDocumentMap(templateId),
  })

  useEffect(() => {
    if (data) {
      const html = data.template_html || ''
      setIsDraft(data.is_draft)
      // The backend's example_analysis_json is arbitrarily shaped resume
      // data - no fixed schema, see field_schema.py's own design note.
      setExampleAnalysisJson((data.example_analysis_json ?? null) as unknown as JsonValue)
      setInitialHtml(html)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // --- Free-form editing: protect bound spans, show literal {{field}} text ---

  const protectBoundSpans = useCallback(() => {
    containerRef.current?.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => {
      el.contentEditable = 'false'
    })
  }, [])

  const populateDisplayValues = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    container.querySelectorAll<HTMLElement>('[data-repeat]').forEach((el) => {
      const count = resolveRepeatArrayLength(el, exampleAnalysisJson)
      if (count !== null) el.setAttribute('data-repeat-count', String(count))
      else el.removeAttribute('data-repeat-count')
    })
    container.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => {
      const path = el.getAttribute('data-bind')
      if (!path) return
      if (path === '.') {
        const groupEl = el.closest('[data-repeat]')
        const fieldName = groupEl?.getAttribute('data-repeat') || '.'
        el.textContent = `{{${fieldName}}}`
      } else {
        el.textContent = `{{${path}}}`
      }
    })
  }, [exampleAnalysisJson])

  const refreshEditorDisplay = useCallback(() => {
    protectBoundSpans()
    populateDisplayValues()
    setDocVersion((v) => v + 1)
  }, [protectBoundSpans, populateDisplayValues])

  useEffect(() => {
    if (!initialHtml) return
    // Setting the initial content here (imperatively, once) instead of via
    // the container's JSX `dangerouslySetInnerHTML` is required, not
    // stylistic: confirmed by direct MutationObserver evidence that React
    // re-applies `dangerouslySetInnerHTML` - wholesale-replacing the live,
    // edited DOM with this same stale `initialHtml` string - on ANY
    // re-render of this component, including ones triggered by completely
    // unrelated state (e.g. docVersion from a toolbar command's
    // refreshEditorDisplay call, which is what made Bold/Underline/
    // Strikethrough appear to silently "undo" themselves immediately after
    // being applied). The comment already documented the intent - "the DOM
    // is the source of truth and is mutated directly, not re-rendered" -
    // this makes that actually true instead of only true when this
    // component happens not to re-render.
    if (containerRef.current) containerRef.current.innerHTML = initialHtml
    refreshEditorDisplay()
    // Only re-run when the document is first populated - afterwards the DOM
    // is the source of truth and is mutated directly, not re-rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml])

  // withBoundSpansEditable protects execCommand (toolbar clicks) from the
  // contenteditable="false" bound-span-island unreliability described
  // above it, but plain keyboard editing (typing, Backspace, Delete, native
  // selection-delete, paste) never goes through that function at all - it's
  // handled entirely by the browser's own native contenteditable behavior.
  // Confirmed in practice: deleting a selection that passes near/through a
  // data-bind span while it's still contentEditable="false" doesn't just
  // fail to delete that one span - it can cascade and wipe out unrelated
  // content throughout the rest of the document. Lifting the same
  // protection for the duration of any native edit (beforeinput through
  // input) closes that gap the same way withBoundSpansEditable does for
  // toolbar commands.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const liftProtection = () => {
      suppressSelectionChangeRef.current = true
      if (suppressSelectionChangeTimeoutRef.current) clearTimeout(suppressSelectionChangeTimeoutRef.current)
      container.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => (el.contentEditable = 'true'))
    }
    const restoreProtection = () => {
      // liftProtection (bound to 'beforeinput') doesn't fire for
      // execCommand-driven changes - only native keystrokes dispatch
      // 'beforeinput'. Arming the suppression here too covers toolbar
      // commands (Bold/Underline/Strikethrough/...), which also fire
      // 'input' synchronously from execCommand and go through this same
      // offset-based restore.
      suppressSelectionChangeRef.current = true
      // Capture the caret/selection (as a character offset - see
      // captureSelectionOffsets below) before any of the DOM mutations below
      // run, since populateDisplayValues and unwrapRedundantSpans both
      // replace live nodes and would otherwise silently reset it.
      const selectionBefore = window.getSelection()
      const anchorBlock = (selectionBefore?.anchorNode?.nodeType === Node.ELEMENT_NODE
        ? (selectionBefore.anchorNode as HTMLElement)
        : selectionBefore?.anchorNode?.parentElement
      )?.closest('p, li, h1, h2, h3, h4, h5, h6, div, td, th') as HTMLElement | null
      const capturedSelection = anchorBlock ? captureSelectionOffsets(anchorBlock) : null

      protectBoundSpans()
      populateDisplayValues()
      // Same span-accumulation this component already works around after a
      // toolbar command (see unwrapRedundantSpans below) also happens from
      // plain native typing, one attribute-less wrapper <span> per keystroke,
      // when typing inside a region with a nearby contentEditable="false"
      // island - confirmed in practice reaching ~19 levels deep from typing
      // alone. That was never cleaned up here before, only after an explicit
      // format() call, so nesting from typing alone could grow unbounded for
      // the life of the editing session. Scoped to the nearest block ancestor
      // of the current selection, same scope format() already uses, so this
      // never walks the whole document on every keystroke.
      if (anchorBlock) unwrapRedundantSpans(anchorBlock)
      if (anchorBlock && capturedSelection) restoreSelectionOffsets(anchorBlock, capturedSelection)
      // Keep suppressing selectionchange for a short idle window after this
      // keystroke - the next keystroke's own beforeinput re-arms it anyway,
      // this only matters for the gap between keystrokes and for the very
      // last keystroke of a burst.
      if (suppressSelectionChangeTimeoutRef.current) clearTimeout(suppressSelectionChangeTimeoutRef.current)
      suppressSelectionChangeTimeoutRef.current = setTimeout(() => {
        suppressSelectionChangeRef.current = false
      }, 250)
    }
    container.addEventListener('beforeinput', liftProtection)
    container.addEventListener('input', restoreProtection)
    return () => {
      container.removeEventListener('beforeinput', liftProtection)
      container.removeEventListener('input', restoreProtection)
    }
  }, [initialHtml, protectBoundSpans, populateDisplayValues])

  // --- Document summary reporting (live counts read from the DOM) ---

  const boundVariableCount = useMemo(
    () => containerRef.current?.querySelectorAll('[data-bind]').length ?? 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docVersion]
  )
  const repeatingGroupCount = useMemo(
    () => containerRef.current?.querySelectorAll('[data-repeat]').length ?? 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docVersion]
  )
  const mappedFieldPaths = useMemo(() => {
    const paths = new Set<string>()
    const container = containerRef.current
    if (!container) return paths
    container.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => {
      const path = el.getAttribute('data-bind')
      if (path) paths.add(path)
    })
    container.querySelectorAll<HTMLElement>('[data-repeat]').forEach((el) => {
      const path = el.getAttribute('data-repeat')
      if (path) paths.add(path)
    })
    return paths
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docVersion])
  const suggestedUnusedFieldsByCategory = useMemo(() => {
    const all = flattenScalarFields(exampleAnalysisJson)
    const byCategory = new Map<string, VariableOption[]>()
    for (const field of all) {
      if (mappedFieldPaths.has(field.path)) continue
      const category = field.path.split('.')[0]
      const list = byCategory.get(category) ?? []
      list.push(field)
      byCategory.set(category, list)
    }
    return Array.from(byCategory.entries()).map(([category, fields]) => ({ category, fields }))
  }, [mappedFieldPaths, exampleAnalysisJson])

  // --- Click-to-review interaction (rebind/unbind an EXISTING bound span) ---

  const closePopover = () => {
    setActiveNodeElement(null)
    setActiveNodeGroupElement(null)
    setActiveNodeCurrentBind(null)
    setCustomFieldPathDraft('')
  }

  const handleDocumentClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement
    const bindEl = target.closest('[data-bind]') as HTMLElement | null
    if (!bindEl) return // ordinary text - let contenteditable place the cursor natively
    event.preventDefault()
    event.stopPropagation()
    setActiveNodeGroupElement(bindEl.closest('[data-repeat]') as HTMLElement | null)
    setActiveNodeCurrentBind(bindEl.getAttribute('data-bind'))
    setActiveNodeElement(bindEl)
    setStaticTextDraft('')
    setCustomFieldPathDraft('')
    setPopoverX(event.clientX)
    setPopoverY(event.clientY)
  }

  const popoverFieldOptions = useMemo<VariableOption[]>(() => {
    if (activeNodeGroupElement) {
      const shape = resolveRepeatItemShape(activeNodeGroupElement, exampleAnalysisJson)
      const fromShape = shape ? flattenScalarFields(shape) : []
      return fromShape.length ? fromShape : discoveredSubFieldOptions(activeNodeGroupElement)
    }
    return flattenScalarFields(exampleAnalysisJson)
  }, [activeNodeGroupElement, exampleAnalysisJson])

  const activeGroupLabel = activeNodeGroupElement?.getAttribute('data-repeat') ?? ''

  const assignVariable = (fieldPath: string) => {
    if (!activeNodeElement) return
    activeNodeElement.setAttribute('data-bind', fieldPath)
    activeNodeElement.contentEditable = 'false'
    closePopover()
    refreshEditorDisplay()
  }

  const assignCustomFieldPath = () => {
    const path = customFieldPathDraft.trim()
    if (!path) return
    if (!isValidFieldForContext(path, activeNodeGroupElement, exampleAnalysisJson)) {
      toast.error(
        `"${path}" is not a field on this repeating section's items - check the Variables panel for the exact field names available here.`
      )
      return
    }
    assignVariable(path)
  }

  const confirmMakeStatic = () => {
    if (!activeNodeElement) return
    activeNodeElement.removeAttribute('data-bind')
    activeNodeElement.textContent = staticTextDraft
    activeNodeElement.contentEditable = 'true'
    closePopover()
    refreshEditorDisplay()
  }

  // --- Rich text formatting (document.execCommand on the Selection/Range) ---

  // Bound variable text (and every date-range/field inside a repeat's
  // example row) lives in a <span data-bind> marked contentEditable="false"
  // so it can never be typed into directly. But browsers are unreliable
  // running execCommand when the current selection touches a
  // contenteditable="false" island - it can silently no-op, or apply once
  // and then refuse to toggle back off/on, or fail to align a whole
  // paragraph that happens to contain one. Temporarily lifting the
  // protection for the duration of the command sidesteps that entirely -
  // the command itself only changes surrounding style/structure, never the
  // bound span's own text, so this can never desync the {{field}}
  // placeholder from its binding.
  const withBoundSpansEditable = (run: () => void) => {
    const container = containerRef.current
    const boundSpans = container ? Array.from(container.querySelectorAll<HTMLElement>('[data-bind]')) : []
    boundSpans.forEach((el) => (el.contentEditable = 'true'))
    try {
      run()
    } finally {
      boundSpans.forEach((el) => (el.contentEditable = 'false'))
    }
  }

  const isPartialLineSelection = (): boolean => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false
    const range = selection.getRangeAt(0)
    const selectedText = range.toString().trim()
    if (!selectedText) return false
    let node: Node | null = range.commonAncestorContainer
    if (node.nodeType !== Node.ELEMENT_NODE) node = node.parentElement
    const blockEl = (node as HTMLElement | null)?.closest('p, li, h1, h2, h3, h4, h5, h6, div, td, th')
    if (!blockEl) return false
    const fullText = (blockEl.textContent || '').trim()
    return selectedText.length < fullText.length
  }

  // Wrapping the selection in a marked span pushes just that fragment right/
  // center without moving the rest of the line - the backend converts it
  // into a real Word tab stop. Returns false if there's no usable selection.
  const alignSelectionInline = (mode: 'right' | 'center'): boolean => {
    const selection = window.getSelection()
    const container = containerRef.current
    if (
      !selection ||
      selection.isCollapsed ||
      selection.rangeCount === 0 ||
      !container ||
      !container.contains(selection.anchorNode)
    ) {
      return false
    }
    const range = selection.getRangeAt(0)
    const fragment = range.extractContents()
    const span = document.createElement('span')
    if (mode === 'right') span.style.float = 'right'
    else span.setAttribute('data-inline-align', 'center')
    span.appendChild(fragment)
    range.insertNode(span)
    selection.removeAllRanges()
    refreshEditorDisplay()
    return true
  }

  const alignSelectionRight = () => {
    if (!alignSelectionInline('right')) {
      toast('Select the text you want pushed to the right of the line first (e.g. the date range), then click this again.', {
        icon: '⚠️',
      })
    }
  }

  // execCommand accumulates a wrapper <span> around the same content on every
  // single call, never cleaning up after itself - formatting the same
  // heading repeatedly across an editing session (bold, then underline,
  // then subscript, ...) piles up an ever-deeper stack of empty, attribute-
  // less spans around it (observed in practice: 9 levels deep after a
  // handful of commands on the same text). Collapsing any span that has no
  // attributes of its own (so never a [data-bind] span, which always
  // carries data-bind/contentEditable) and wraps exactly one child element
  // keeps the DOM sane after every command instead of letting it grow
  // unbounded for the life of the editing session.
  const unwrapRedundantSpans = (root: HTMLElement) => {
    const spans = Array.from(root.querySelectorAll('span'))
    for (let i = spans.length - 1; i >= 0; i--) {
      const span = spans[i]
      if (span.attributes.length === 0 && span.childNodes.length === 1 && span.firstChild?.nodeType === Node.ELEMENT_NODE) {
        span.replaceWith(span.firstChild)
      }
    }
  }

  // unwrapRedundantSpans replaces live DOM nodes, which invalidates any
  // Selection/Range still pointing into the replaced node and makes the
  // browser collapse the caret to an unrelated position (confirmed in
  // practice: typing "PWTEST956127" one keystroke at a time while this ran
  // on every 'input' event produced "ST956127EAWS..." - each new
  // keystroke's caret reset caused it to insert before the previous one
  // instead of after it). Capturing the caret/selection as a plain
  // character offset relative to `root` before the unwrap and restoring it
  // from that same offset afterward survives the node replacement because
  // unwrapping only removes redundant wrapper elements - it never changes
  // root's text content or character count.
  const charOffsetWithin = (root: HTMLElement, node: Node, offset: number) => {
    const range = document.createRange()
    range.selectNodeContents(root)
    range.setEnd(node, offset)
    return range.toString().length
  }

  const captureSelectionOffsets = (root: HTMLElement) => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.anchorNode || !selection.focusNode) return null
    if (!root.contains(selection.anchorNode) || !root.contains(selection.focusNode)) return null
    return {
      anchorOffset: charOffsetWithin(root, selection.anchorNode, selection.anchorOffset),
      focusOffset: charOffsetWithin(root, selection.focusNode, selection.focusOffset),
    }
  }

  const positionForCharOffset = (root: HTMLElement, charOffset: number): { node: Node; offset: number } | null => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let remaining = charOffset
    let lastTextNode: Text | null = null
    let current: Node | null
    while ((current = walker.nextNode())) {
      const textNode = current as Text
      lastTextNode = textNode
      const length = textNode.textContent?.length ?? 0
      if (remaining <= length) return { node: textNode, offset: remaining }
      remaining -= length
    }
    return lastTextNode ? { node: lastTextNode, offset: lastTextNode.textContent?.length ?? 0 } : null
  }

  const restoreSelectionOffsets = (
    root: HTMLElement,
    captured: { anchorOffset: number; focusOffset: number }
  ) => {
    const anchorPosition = positionForCharOffset(root, captured.anchorOffset)
    const focusPosition = positionForCharOffset(root, captured.focusOffset)
    if (!anchorPosition || !focusPosition) return
    window.getSelection()?.setBaseAndExtent(anchorPosition.node, anchorPosition.offset, focusPosition.node, focusPosition.offset)
  }

  const format = (command: string, value?: string) => {
    if ((command === 'justifyCenter' || command === 'justifyRight') && isPartialLineSelection()) {
      alignSelectionInline(command === 'justifyRight' ? 'right' : 'center')
      return
    }
    containerRef.current?.focus()
    const selBefore = window.getSelection()
    const anchorBlock = (selBefore?.anchorNode?.nodeType === Node.ELEMENT_NODE
      ? (selBefore.anchorNode as HTMLElement)
      : selBefore?.anchorNode?.parentElement
    )?.closest('p, li, h1, h2, h3, h4, h5, h6, div, td, th')
    withBoundSpansEditable(() => {
      document.execCommand(command, false, value)
    })
    if (anchorBlock) {
      unwrapRedundantSpans(anchorBlock as HTMLElement)
      // A bare <u>/<s>/<strike> relies on the browser's own default
      // decoration thickness, which is thin enough (1 logical px) that it
      // can round away to nothing under fractional OS display scaling
      // (125%/150%, common on Windows) - getComputedStyle still reports
      // "underline"/"line-through" correctly in that case (it's a logical
      // CSS value, not the painted pixel result), which is exactly why
      // that alone was never enough to prove it was actually visible.
      // Setting an explicit thickness sidesteps relying on the browser's
      // default at all.
      if (command === 'underline' || command === 'strikeThrough') {
        anchorBlock.querySelectorAll('u, s, strike').forEach((el) => {
          const style = (el as HTMLElement).style
          style.textDecorationThickness = '2px'
          style.textUnderlineOffset = command === 'underline' ? '2px' : ''
        })
      }
    }
    refreshEditorDisplay()
  }

  const setTextColor = (color: string) => format('foreColor', color)
  const setHeading = (tag: string) => format('formatBlock', `<${tag}>`)

  const currentFontSizePt = (): number => {
    const selection = window.getSelection()
    const node: Node | null = selection && selection.rangeCount ? selection.getRangeAt(0).startContainer : null
    const el = node ? (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement) : null
    if (!el) return 11
    const sizePx = parseFloat(window.getComputedStyle(el).fontSize || '14.6667')
    return Math.round(sizePx * 0.75 * 2) / 2
  }

  // Keeps the font-size dropdown showing the size at wherever the cursor/
  // selection actually is, the same way a real word processor's toolbar
  // reflects live formatting state - updates on every selection change
  // anywhere in the document (typing, clicking, arrow-key navigation),
  // not just after an explicit size change from this toolbar.
  useEffect(() => {
    const handleSelectionChange = () => {
      // Confirmed by direct testing: this listener also fires for the
      // JS-driven selection restore in restoreProtection's offset-based
      // caret fix (setBaseAndExtent dispatches selectionchange like any
      // other selection change), and even doing nothing but a read-only
      // getComputedStyle read here occasionally landed between two
      // keystrokes closely enough to disrupt the caret restoreSelectionOffsets
      // had just placed, scrambling typed character order. Skipping this
      // handler for the duration of an active native edit (see
      // suppressSelectionChangeRef above) removes that race; it still
      // updates normally the moment typing pauses or the selection changes
      // by click/arrow-key navigation instead.
      if (suppressSelectionChangeRef.current) return
      const container = containerRef.current
      const selection = window.getSelection()
      if (!container || !selection || selection.rangeCount === 0) return
      if (!container.contains(selection.getRangeAt(0).startContainer)) return
      const pt = currentFontSizePt()
      setFontSizeDisplayPt((prev) => (prev === pt ? prev : pt))
    }
    document.addEventListener('selectionchange', handleSelectionChange)
    return () => document.removeEventListener('selectionchange', handleSelectionChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const applyFontSize = (pt: number) => {
    containerRef.current?.focus()
    withBoundSpansEditable(() => document.execCommand('fontSize', false, '7'))
    containerRef.current?.querySelectorAll('font[size="7"]').forEach((el) => {
      const span = document.createElement('span')
      span.style.fontSize = `${pt}pt`
      span.innerHTML = el.innerHTML
      el.replaceWith(span)
    })
    setFontSizeDisplayPt(pt)
    refreshEditorDisplay()
  }

  const increaseFontSize = () => {
    const current = currentFontSizePt()
    const next = FONT_SIZE_STEPS_PT.find((s) => s > current) ?? FONT_SIZE_STEPS_PT[FONT_SIZE_STEPS_PT.length - 1]
    applyFontSize(next)
  }
  const decreaseFontSize = () => {
    const current = currentFontSizePt()
    const steps = [...FONT_SIZE_STEPS_PT].reverse()
    const next = steps.find((s) => s < current) ?? FONT_SIZE_STEPS_PT[0]
    applyFontSize(next)
  }

  const setHighlightColor = (color: string) => {
    containerRef.current?.focus()
    withBoundSpansEditable(() => {
      if (!document.execCommand('hiliteColor', false, color)) {
        document.execCommand('backColor', false, color)
      }
    })
    refreshEditorDisplay()
  }

  // --- Table toolbar menu ---

  const closeTableMenu = () => setTableMenuOpen(false)

  const currentCellForMenu = (): HTMLTableCellElement | null => {
    const selection = window.getSelection()
    const container = containerRef.current
    if (!selection || !container) return null
    const node = selection.anchorNode
    const el = node ? (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement) : null
    if (!el || !container.contains(el)) return null
    return el.closest('td, th')
  }

  const openTableMenu = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const cell = currentCellForMenu()
    menuCellRef.current = cell
    setTableMenuHasCellContext(!!cell)
    setTableMenuOpen(true)
    setTableMenuX(Math.min(rect.left, window.innerWidth - 260))
    setTableMenuY(rect.bottom + 4)
  }

  const newTableCell = (): HTMLTableCellElement => {
    const td = document.createElement('td')
    td.innerHTML = '&nbsp;'
    td.style.border = '1px solid #999'
    td.style.padding = '6px'
    return td
  }

  const insertTable = (rows = 2, cols = 2) => {
    let html = '<table style="border-collapse:collapse;width:100%;" border="1">'
    for (let r = 0; r < rows; r++) {
      html += '<tr>'
      for (let c = 0; c < cols; c++) html += '<td style="padding:6px;border:1px solid #999;">&nbsp;</td>'
      html += '</tr>'
    }
    html += '</table><p><br></p>'
    format('insertHTML', html)
    closeTableMenu()
  }

  const insertTableRow = (position: 'above' | 'below') => {
    const row = menuCellRef.current?.closest('tr')
    if (!row) {
      toast('Click inside a table cell first, then use this option.', { icon: '⚠️' })
      return
    }
    const newRow = document.createElement('tr')
    for (let i = 0; i < row.children.length; i++) newRow.appendChild(newTableCell())
    row.parentElement?.insertBefore(newRow, position === 'above' ? row : row.nextSibling)
    closeTableMenu()
    refreshEditorDisplay()
  }

  const insertTableColumn = (position: 'left' | 'right') => {
    const cell = menuCellRef.current
    const row = cell?.closest('tr')
    const table = cell?.closest('table')
    if (!cell || !row || !table) {
      toast('Click inside a table cell first, then use this option.', { icon: '⚠️' })
      return
    }
    const colIndex = Array.from(row.children).indexOf(cell)
    table.querySelectorAll('tr').forEach((tr) => {
      const refCell = tr.children[colIndex] as HTMLElement | undefined
      const newCell = newTableCell()
      if (position === 'left') {
        if (refCell) tr.insertBefore(newCell, refCell)
        else tr.appendChild(newCell)
      } else if (refCell?.nextElementSibling) {
        tr.insertBefore(newCell, refCell.nextElementSibling)
      } else {
        tr.appendChild(newCell)
      }
    })
    closeTableMenu()
    refreshEditorDisplay()
  }

  const deleteTableRow = () => {
    const row = menuCellRef.current?.closest('tr')
    const table = menuCellRef.current?.closest('table')
    if (!row || !table) {
      toast('Click inside a table cell first, then use this option.', { icon: '⚠️' })
      return
    }
    if (table.querySelectorAll('tr').length <= 1) {
      toast('A table needs at least one row - delete the whole table instead (right-click inside it).', { icon: '⚠️' })
      return
    }
    row.remove()
    closeTableMenu()
    refreshEditorDisplay()
  }

  const deleteTableColumn = () => {
    const cell = menuCellRef.current
    const row = cell?.closest('tr')
    const table = cell?.closest('table')
    if (!cell || !row || !table) {
      toast('Click inside a table cell first, then use this option.', { icon: '⚠️' })
      return
    }
    if (row.children.length <= 1) {
      toast('A table needs at least one column - delete the whole table instead (right-click inside it).', { icon: '⚠️' })
      return
    }
    const colIndex = Array.from(row.children).indexOf(cell)
    table.querySelectorAll('tr').forEach((tr) => tr.children[colIndex]?.remove())
    closeTableMenu()
    refreshEditorDisplay()
  }

  const setTableCellBackground = (color: string) => {
    if (!menuCellRef.current) {
      toast('Click inside a table cell first, then pick a shading color.', { icon: '⚠️' })
      return
    }
    menuCellRef.current.style.backgroundColor = color
    refreshEditorDisplay()
  }

  const toggleTableBorders = (show: boolean) => {
    const table = menuCellRef.current?.closest('table')
    if (!table) {
      toast('Click inside a table cell first, then use this option.', { icon: '⚠️' })
      return
    }
    table.querySelectorAll('td, th').forEach((c) => {
      ;(c as HTMLElement).style.border = show ? '1px solid #999' : 'none'
    })
    closeTableMenu()
    refreshEditorDisplay()
  }

  const removeTableEntirely = (table: HTMLTableElement) => table.remove()

  const unwrapTableKeepContent = (table: HTMLTableElement) => {
    const fragment = document.createDocumentFragment()
    table.querySelectorAll('tr').forEach((tr) => {
      const cellsHtml = Array.from(tr.querySelectorAll('td, th'))
        .map((cell) => (cell as HTMLElement).innerHTML.trim())
        .filter((html) => html && html !== '&nbsp;')
      if (!cellsHtml.length) return
      const p = document.createElement('p')
      p.innerHTML = cellsHtml.join('&nbsp;&nbsp;&nbsp;')
      fragment.appendChild(p)
    })
    table.replaceWith(fragment)
  }

  // --- Variable insertion: drag-and-drop and right-click ---

  const insertBoundSpanAtRange = (range: Range, fieldPath: string) => {
    range.deleteContents()
    const span = document.createElement('span')
    span.setAttribute('data-bind', fieldPath)
    span.setAttribute('contenteditable', 'false')
    span.textContent = `{{${fieldPath}}}`
    range.insertNode(span)
    range.setStartAfter(span)
    range.collapse(true)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    refreshEditorDisplay()
  }

  const insertHtmlAtRange = (range: Range, html: string) => {
    range.deleteContents()
    const fragment = range.createContextualFragment(html)
    const lastNode = fragment.lastChild
    range.insertNode(fragment)
    if (lastNode) {
      range.setStartAfter(lastNode)
      range.collapse(true)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
    refreshEditorDisplay()
  }

  const handleJsonTreeDrop = (range: Range, payload: JsonTreeDragPayload) => {
    if (payload.arrayRootPath) {
      const closestGroup = closestGroupForNode(range.startContainer)
      if (!closestGroup || closestGroup.getAttribute('data-repeat') !== payload.arrayRootPath) {
        toast(
          `"${payload.label}" only renders inside the "${payload.arrayRootPath}" repeating section - it was dropped outside it, so it will render blank until moved there.`,
          { icon: '⚠️' }
        )
      }
    }

    if (payload.kind === 'scalar') {
      insertBoundSpanAtRange(range, payload.insertValue)
      return
    }
    const html =
      payload.kind === 'array-of-objects'
        ? `<div data-repeat="${payload.insertValue}"><p>Drag fields from the "${payload.label}" example item in the Variables panel into here</p></div>`
        : `<span data-repeat="${payload.insertValue}"><span data-bind=".">{{.}}</span><span>, </span></span>`
    insertHtmlAtRange(range, html)
  }

  const handleDocumentDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  const handleDocumentDrop = (event: React.DragEvent) => {
    event.preventDefault()
    const range = caretRangeFromPoint(event.clientX, event.clientY)
    if (!range) {
      toast.error('Could not determine a drop position - try dropping directly on a line of text.')
      return
    }
    const jsonPayload = event.dataTransfer.getData('application/json')
    if (jsonPayload) {
      try {
        handleJsonTreeDrop(range, JSON.parse(jsonPayload) as JsonTreeDragPayload)
        return
      } catch {
        // fall through to plain-text handling below
      }
    }
    const fieldPath = event.dataTransfer.getData('text/plain')
    if (!fieldPath) return
    insertBoundSpanAtRange(range, fieldPath)
  }

  // --- Right-click "quick format + insert variable" menu ---

  const closestTableForNode = (node: Node | null): HTMLTableElement | null => {
    const el = node ? (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement) : null
    const container = containerRef.current
    if (!el || !container || !container.contains(el)) return null
    return el.closest('table')
  }

  const closeVariableMenu = () => {
    setVariableMenuOpen(false)
    savedInsertRangeRef.current = null
    savedGroupElRef.current = null
    savedTableRef.current = null
  }

  const handleDocumentContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    const selection = window.getSelection()
    const hasRealSelection =
      !!selection && !selection.isCollapsed && selection.rangeCount > 0 && containerRef.current?.contains(selection.anchorNode)
    const range = hasRealSelection ? selection!.getRangeAt(0) : caretRangeFromPoint(event.clientX, event.clientY)
    if (!range) return
    savedInsertRangeRef.current = range
    savedGroupElRef.current = closestGroupForNode(range.startContainer)
    savedTableRef.current = closestTableForNode(range.startContainer)
    setVariableMenuOpen(true)
    setVariableMenuSearch('')
    setVariableMenuX(Math.min(event.clientX, window.innerWidth - 300))
    setVariableMenuY(Math.min(event.clientY, window.innerHeight - 420))
  }

  const deleteTableFromMenu = () => {
    if (savedTableRef.current) removeTableEntirely(savedTableRef.current)
    closeVariableMenu()
    refreshEditorDisplay()
  }

  const unwrapTableFromMenu = () => {
    if (savedTableRef.current) unwrapTableKeepContent(savedTableRef.current)
    closeVariableMenu()
    refreshEditorDisplay()
  }

  const variableMenuOptions = useMemo<VariableOption[]>(() => {
    const groupEl = savedGroupElRef.current
    let all: VariableOption[]
    if (groupEl) {
      const shape = resolveRepeatItemShape(groupEl, exampleAnalysisJson)
      all = shape ? flattenScalarFields(shape) : discoveredSubFieldOptions(groupEl)
    } else {
      all = flattenScalarFields(exampleAnalysisJson)
    }
    const term = variableMenuSearch.trim().toLowerCase()
    if (!term) return all
    return all.filter((f) => f.label.toLowerCase().includes(term) || f.path.toLowerCase().includes(term))
    // savedGroupElRef is set synchronously right before variableMenuOpen flips true, so
    // that dependency is what triggers recomputation of the ref-derived value here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableMenuOpen, variableMenuSearch, exampleAnalysisJson])

  const chooseVariableFromMenu = (fieldPath: string) => {
    if (savedInsertRangeRef.current) insertBoundSpanAtRange(savedInsertRangeRef.current, fieldPath)
    closeVariableMenu()
  }

  const submitCustomVariableFromMenu = () => {
    const path = variableMenuSearch.trim()
    if (!path) return
    if (!isValidFieldForContext(path, savedGroupElRef.current, exampleAnalysisJson)) {
      toast.error(`"${path}" is not a field on this repeating section's items.`)
      return
    }
    chooseVariableFromMenu(path)
  }

  // --- Toolbar "Repeat" button menu ---

  const closeRepeatMenu = () => {
    setRepeatMenuOpen(false)
    savedInsertRangeRef.current = null
    savedGroupElRef.current = null
  }

  const openRepeatMenu = () => {
    const selection = window.getSelection()
    const range =
      selection && selection.rangeCount > 0 && containerRef.current?.contains(selection.anchorNode)
        ? selection.getRangeAt(0)
        : null
    if (!range) {
      toast('Click inside the document first to choose where to insert the repeating section.', { icon: '⚠️' })
      return
    }
    savedInsertRangeRef.current = range
    savedGroupElRef.current = closestGroupForNode(range.startContainer)
    const rect = range.getClientRects()[0] ?? range.startContainer.parentElement?.getBoundingClientRect()
    setRepeatMenuX(rect ? rect.left : window.innerWidth / 2 - 140)
    setRepeatMenuY(rect ? rect.bottom + 4 : window.innerHeight / 2 - 100)
    setRepeatMenuOpen(true)
  }

  const repeatMenuOptions = useMemo<VariableOption[]>(() => {
    const groupEl = savedGroupElRef.current
    if (groupEl) {
      const shape = resolveRepeatItemShape(groupEl, exampleAnalysisJson)
      return shape ? flattenArrayFields(shape) : []
    }
    return flattenArrayFields(exampleAnalysisJson)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatMenuOpen, exampleAnalysisJson])

  const chooseRepeatFromMenu = (path: string) => {
    if (!savedInsertRangeRef.current) return
    const arr = resolveArrayByPath(path, savedGroupElRef.current, exampleAnalysisJson)
    const isObjectArray = !!arr && arr.length > 0 && isPlainObject(arr[0])
    const html = isObjectArray
      ? `<div data-repeat="${path}"><p>Drag fields from the Variables panel into here</p></div>`
      : `<span data-repeat="${path}"><span data-bind=".">{{.}}</span><span>, </span></span>`
    insertHtmlAtRange(savedInsertRangeRef.current, html)
    closeRepeatMenu()
  }

  // --- Split layout: document+JSON half | resizable, closable preview half ---

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault()
    resizingRef.current = true
    const onMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return
      const container = splitContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100
      setLeftPaneWidthPercent(Math.min(80, Math.max(30, pct)))
    }
    const onUp = () => {
      resizingRef.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // --- Persisting the reviewed document ---

  // The backend re-runs its own node-id annotation pass on whatever HTML is
  // sent back, so data-node-id must be stripped before sending (otherwise
  // every save nests another wrapper layer). contenteditable/data-repeat-count
  // are pure UI affordances. This operates on a detached CLONE so the live
  // document isn't mutated. Every bound span's displayed example VALUE is
  // rewritten back to its literal "{{path}}" form here - the backend's
  // render_mustache works off that literal text, never the data-bind attribute.
  const buildPayload = (): string | null => {
    const container = containerRef.current
    if (!container) return null
    const clone = container.cloneNode(true) as HTMLElement
    clone.querySelectorAll('[data-node-id]').forEach((el) => el.removeAttribute('data-node-id'))
    clone.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'))
    clone.querySelectorAll('[data-repeat-count]').forEach((el) => el.removeAttribute('data-repeat-count'))
    clone.querySelectorAll<HTMLElement>('[data-bind]').forEach((el) => {
      const path = el.getAttribute('data-bind')
      if (path) el.textContent = `{{${path}}}`
    })
    clone.removeAttribute('contenteditable')
    return clone.innerHTML
  }

  const saveMutation = useMutation<ResumeFormatTemplate, unknown, boolean>({
    mutationFn: async (activate) => {
      const html = buildPayload()
      if (html === null) throw new Error('The document is not ready yet.')
      return saveDocumentMap(templateId, html, activate)
    },
    onSuccess: (_result, activate) => {
      toast.success(activate ? 'Document map saved. Template is now active.' : 'Draft saved - the template is not live yet.')
      // Without this, react-query keeps serving the pre-save cached response
      // for this exact templateId if the editor is reopened within the
      // default staleTime window - the save genuinely persisted, but the
      // reopened editor would show the stale pre-edit content instead of
      // what was just saved.
      queryClient.invalidateQueries({ queryKey: ['resume-template-document-map', templateId] })
      onClose()
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to save document map.'))
    },
  })

  const testMutation = useMutation<Blob, unknown, Record<string, unknown>>({
    mutationFn: async (resumeAnalysis) => {
      const html = buildPayload()
      if (html === null) throw new Error('The document is not ready yet.')
      return testDocumentMapWithResume(templateId, html, resumeAnalysis)
    },
    onSuccess: (blob) => {
      downloadBlob(blob, 'test_formatted_resume.docx')
      setTestJsonModalOpen(false)
      toast.success('Test resume formatted successfully.')
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to format the test resume.'))
    },
  })

  const submitTestJson = () => {
    let resumeAnalysis: Record<string, unknown>
    try {
      resumeAnalysis = JSON.parse(testJsonDraft) as Record<string, unknown>
    } catch {
      toast.error('That is not valid JSON. Please check the payload and try again.')
      return
    }
    testMutation.mutate(resumeAnalysis)
  }

  // Renders the actual docx into real, paginated Word-like page divs via
  // docx-preview (matching how the downloaded file will look), falling back
  // to a plain download if inline rendering fails for any reason.
  const refreshPreview = useCallback(async () => {
    const html = buildPayload()
    if (html === null) return
    setPreviewing(true)
    setPreviewError(null)
    setPreviewRendered(false)
    try {
      const blob = await previewDocumentMap(templateId, html)
      const arrayBuffer = await blob.arrayBuffer()
      const container = docxPreviewContainerRef.current
      if (!container) {
        setPreviewing(false)
        setPreviewError('Failed to render preview.')
        return
      }
      container.innerHTML = ''
      try {
        const docxModule = await import('docx-preview')
        await docxModule.renderAsync(arrayBuffer, container)
        setPreviewRendered(true)
      } catch {
        setPreviewError('Failed to render an inline preview - use Download instead.')
      } finally {
        setPreviewing(false)
      }
    } catch (err) {
      setPreviewing(false)
      setPreviewError(getErrorMessage(err, 'Failed to render preview.'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId])

  const downloadPreview = async () => {
    const html = buildPayload()
    if (html === null) return
    try {
      const blob = await previewDocumentMap(templateId, html)
      downloadBlob(blob, 'template_preview.docx')
      toast.success('Preview downloaded.')
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate preview.'))
    }
  }

  const togglePreviewPane = () => setPreviewPaneOpen((open) => !open)

  useEffect(() => {
    if (previewPaneOpen) void refreshPreview()
  }, [previewPaneOpen, refreshPreview])

  // --- Render ---

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-white">
        <LoadingSpinner />
        <span className="ml-3 text-sm text-gray-500">Loading document map...</span>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-white p-8">
        <p className="max-w-md text-center text-sm text-amber-700">{getErrorMessage(error, 'Failed to load document map.')}</p>
        <button onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
          Back to list
        </button>
      </div>
    )
  }

  const objectKeys = isPlainObject(exampleAnalysisJson) ? Object.keys(exampleAnalysisJson) : []

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <style>{DOC_STYLES}</style>

      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-5 border-b border-gray-200 bg-white px-6 py-2.5 shadow-sm">
        <div className="flex flex-shrink-0 items-center gap-2.5">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50">
            <PencilSquareIcon className="h-4.5 w-4.5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-900">Edit Template</h1>
            <p className="text-xs text-gray-500">Confirm or correct every field before this template goes live.</p>
          </div>
        </div>

        <div className="relative flex min-w-0 flex-1 justify-center">
          <button
            type="button"
            onClick={() => setCoveragePanelExpanded((e) => !e)}
            className="flex items-center gap-2.5 rounded-full px-3 py-1 text-xs hover:bg-gray-50"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 font-semibold text-indigo-900">
              {boundVariableCount} Variable{boundVariableCount === 1 ? '' : 's'} Bound
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800">
              <ArrowPathRoundedSquareIcon className="h-3.5 w-3.5" />
              {repeatingGroupCount} Repeat Section{repeatingGroupCount === 1 ? '' : 's'}
            </span>
            {isDraft && (
              <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-1 font-semibold text-yellow-800">Draft</span>
            )}
            {coveragePanelExpanded ? <ChevronDownIcon className="h-3.5 w-3.5 rotate-180" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
          </button>
          {coveragePanelExpanded && (
            <>
              <div className="fixed inset-0 z-[70]" onClick={() => setCoveragePanelExpanded(false)} />
              <div
                className="absolute top-full z-[80] mt-1 max-h-[60vh] w-[420px] max-w-[90vw] overflow-y-auto rounded-md border border-gray-200 bg-white p-4 text-left shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="mb-2 text-xs text-gray-500">
                  Fields from this template's own resume_analysis JSON that aren't bound anywhere yet - purely suggestions, not
                  errors.
                </p>
                {suggestedUnusedFieldsByCategory.length === 0 ? (
                  <div className="py-2 text-sm text-emerald-700">Every field in this JSON is represented in this template.</div>
                ) : (
                  suggestedUnusedFieldsByCategory.map((group) => (
                    <div key={group.category} className="mb-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{group.category}</div>
                      {group.fields.map((f) => (
                        <div key={f.path} className="border-t border-gray-100 py-1 text-sm text-gray-700 first:border-t-0">
                          {f.path}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <QuestionMarkCircleIcon className="mr-1 h-4 w-4" />
            Help
          </button>
          <button
            type="button"
            disabled={testMutation.isPending}
            onClick={() => {
              setTestJsonDraft('')
              setTestJsonModalOpen(true)
            }}
            className="inline-flex items-center rounded-md border border-indigo-300 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            <CodeBracketIcon className="mr-1 h-4 w-4" />
            {testMutation.isPending ? 'Testing...' : 'Test with Resume JSON'}
          </button>
          <button
            type="button"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate(false)}
            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Save as Draft
          </button>
          <button
            type="button"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
            {saveMutation.isPending ? 'Saving...' : 'Save as Active'}
          </button>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-5 py-1">
        <select
          className="h-7 rounded border border-gray-200 px-1.5 text-xs text-gray-700"
          defaultValue="p"
          onChange={(e) => setHeading(e.target.value)}
          title="Paragraph style"
        >
          <option value="p">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
        </select>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title="Decrease font size" onClick={decreaseFontSize}>
          A-
        </ToolbarButton>
        <select
          className="h-7 rounded border border-gray-200 px-1 text-xs text-gray-700"
          value={fontSizeDisplayPt}
          onChange={(e) => applyFontSize(parseFloat(e.target.value))}
          title="Font size (pt)"
        >
          {FONT_SIZE_STEPS_PT.includes(fontSizeDisplayPt) ? null : (
            <option value={fontSizeDisplayPt}>{fontSizeDisplayPt}</option>
          )}
          {FONT_SIZE_STEPS_PT.map((pt) => (
            <option key={pt} value={pt}>
              {pt}
            </option>
          ))}
        </select>
        <ToolbarButton title="Increase font size" onClick={increaseFontSize}>
          A+
        </ToolbarButton>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title="Bold" onClick={() => format('bold')}>
          <BoldIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => format('italic')}>
          <ItalicIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Underline" onClick={() => format('underline')}>
          <span className="underline">U</span>
        </ToolbarButton>
        <ToolbarButton title="Strikethrough" onClick={() => format('strikeThrough')}>
          <span className="line-through">S</span>
        </ToolbarButton>
        <ToolbarButton title="Subscript" onClick={() => format('subscript')}>
          {'X₂'}
        </ToolbarButton>
        <ToolbarButton title="Superscript" onClick={() => format('superscript')}>
          {'X²'}
        </ToolbarButton>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title="Align left" onClick={() => format('justifyLeft')}>
          <Bars3BottomLeftIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Align center" onClick={() => format('justifyCenter')}>
          <Bars3CenterLeftIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Align right" onClick={() => format('justifyRight')}>
          <Bars3BottomRightIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Justify" onClick={() => format('justifyFull')}>
          <Bars3Icon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Push just the selected text (e.g. a date range) to the right edge of this line" onClick={alignSelectionRight}>
          {'⇥'}
        </ToolbarButton>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title="Bullet list" onClick={() => format('insertUnorderedList')}>
          <ListBulletIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => format('insertOrderedList')}>
          1.
        </ToolbarButton>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <label className="flex h-7 w-7 cursor-pointer items-center justify-center rounded font-bold hover:bg-indigo-50" title="Text color">
          A
          <input
            type="color"
            defaultValue="#000000"
            onMouseDown={(e) => e.stopPropagation()}
            onInput={(e) => setTextColor(e.currentTarget.value)}
            className="h-0 w-0 opacity-0"
          />
        </label>
        <label className="flex h-7 w-7 cursor-pointer items-center justify-center rounded hover:bg-indigo-50" title="Highlight color">
          <span className="rounded bg-yellow-200 px-0.5 text-xs">H</span>
          <input
            type="color"
            defaultValue="#FFF176"
            onMouseDown={(e) => e.stopPropagation()}
            onInput={(e) => setHighlightColor(e.currentTarget.value)}
            className="h-0 w-0 opacity-0"
          />
        </label>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title="Table options" onClick={openTableMenu} wide>
          <TableCellsIcon className="h-4 w-4" />
          Table
          <ChevronDownIcon className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton title="Insert a new repeating section at the cursor" onClick={openRepeatMenu} wide>
          <ArrowPathRoundedSquareIcon className="h-4 w-4" />
          Repeat
        </ToolbarButton>
        <span className="mx-1 h-4.5 w-px bg-gray-200" />
        <ToolbarButton title={previewPaneOpen ? 'Close preview' : 'Open preview'} onClick={togglePreviewPane} wide active={previewPaneOpen}>
          {previewPaneOpen ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          {previewPaneOpen ? 'Close Preview' : 'Preview'}
        </ToolbarButton>
        <span className="ml-auto whitespace-nowrap text-xs text-gray-500">
          Right-click anywhere to format or insert a variable, or drag one in from the Variables panel
        </span>
      </div>

      {/* Split: document + JSON tree | preview */}
      <div ref={splitContainerRef} className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-w-0 overflow-y-auto bg-gray-50 p-3.5" style={{ width: previewPaneOpen ? `${leftPaneWidthPercent}%` : '100%' }}>
          <div className="flex items-start gap-5">
            <div className="min-w-0 flex-1">
              <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-indigo-50 to-indigo-50/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-indigo-900">
                  Template Document
                  <span title="Green = bound to a variable. Blue dashed box = repeating block. Type anywhere to edit, click a bound field to reassign it, right-click or drag a variable in to bind new content.">
                    <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-gray-400" />
                  </span>
                </div>
                <div
                  ref={containerRef}
                  className="dme-doc min-h-[300px] p-4 leading-relaxed outline-none focus:shadow-[inset_0_0_0_2px_rgba(85,110,230,0.25)]"
                  contentEditable
                  spellCheck={false}
                  onClick={handleDocumentClick}
                  onDragOver={handleDocumentDragOver}
                  onDrop={handleDocumentDrop}
                  onContextMenu={handleDocumentContextMenu}
                />
              </div>
            </div>

            <div className="sticky top-0 w-80 flex-shrink-0 self-start overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm" style={{ maxHeight: '100vh' }}>
              <div className="sticky top-0 flex items-center gap-1.5 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-indigo-50/40 px-3.5 py-3 text-sm font-bold text-indigo-900">
                Variables
                <span title='This is the actual resume_analysis JSON used to build this template. Drag a field onto the document to bind it, or drag an array&apos;s "+ Repeat Section" chip to insert a brand-new repeating section for it.'>
                  <InformationCircleIcon className="h-4 w-4 cursor-help text-gray-400" />
                </span>
              </div>
              {exampleAnalysisJson ? (
                <div className="px-2.5 pb-3.5">
                  {objectKeys.map((k) => (
                    <JsonTreeNode
                      key={k}
                      keyLabel={k}
                      value={(exampleAnalysisJson as Record<string, JsonValue>)[k]}
                      absolutePath={k}
                      depth={0}
                    />
                  ))}
                </div>
              ) : (
                <p className="p-3.5 text-xs text-gray-500">
                  No example resume_analysis JSON is stored for this template yet - re-prepare it to capture one.
                </p>
              )}
            </div>
          </div>
        </div>

        {previewPaneOpen && (
          <>
            <div className="w-1.5 flex-shrink-0 cursor-col-resize bg-gray-200 hover:bg-primary-500" onMouseDown={startResize} />
            <div className="flex min-w-0 flex-col border-l border-gray-200 bg-white" style={{ width: `${100 - leftPaneWidthPercent}%` }}>
              <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2.5">
                <span className="text-sm font-semibold">Preview</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void refreshPreview()}
                    disabled={previewing}
                    title="Refresh preview"
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${previewing ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={togglePreviewPane}
                    title="Close preview"
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {previewing && <div className="py-10 text-center text-sm text-gray-500">Rendering...</div>}
                {!previewing && previewError && (
                  <div className="m-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <p>{previewError}</p>
                    <button
                      type="button"
                      onClick={() => void downloadPreview()}
                      className="mt-2 rounded border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    >
                      Download instead
                    </button>
                  </div>
                )}
                <div
                  ref={docxPreviewContainerRef}
                  className="dme-docx-preview overflow-x-auto p-4"
                  hidden={previewing || !!previewError || !previewRendered}
                />
                {!previewing && !previewError && !previewRendered && (
                  <p className="p-3 text-xs text-gray-500">Click refresh to render the current document against built-in mock data.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Rebind/unbind popover */}
      {activeNodeElement && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={closePopover} />
          <div
            className="fixed z-[80] min-w-[240px] rounded-md border border-gray-200 bg-white p-2.5 shadow-lg"
            style={{ left: popoverX - 8, top: popoverY + 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            {activeNodeGroupElement && <div className="mb-1 text-xs text-gray-500">Field within: {activeGroupLabel}</div>}
            {popoverFieldOptions.length > 0 && (
              <select
                className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                value={activeNodeCurrentBind ?? ''}
                onChange={(e) => assignVariable(e.target.value)}
              >
                <option value="" disabled>
                  &mdash; assign a variable &mdash;
                </option>
                {popoverFieldOptions.map((f) => (
                  <option key={f.path} value={f.path}>
                    {f.label}
                  </option>
                ))}
              </select>
            )}
            <div className="mb-1 text-xs text-gray-500">
              Or bind to any field path{activeNodeGroupElement ? ' (bare name, e.g. "position")' : ' (e.g. "skills_analysis.technical_skills")'}:
            </div>
            <div className="mb-2 flex gap-1">
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                value={customFieldPathDraft}
                onChange={(e) => setCustomFieldPathDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && assignCustomFieldPath()}
                placeholder="field.path"
              />
              <button
                type="button"
                disabled={!customFieldPathDraft}
                onClick={assignCustomFieldPath}
                className="rounded border border-primary-300 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
              >
                Bind
              </button>
            </div>
            {activeNodeCurrentBind && (
              <>
                <div className="mb-1 text-xs text-gray-500">Or make this static text instead:</div>
                <input
                  type="text"
                  className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  value={staticTextDraft}
                  onChange={(e) => setStaticTextDraft(e.target.value)}
                  placeholder="Type replacement text..."
                />
                <button
                  type="button"
                  onClick={confirmMakeStatic}
                  className="w-full rounded border border-gray-300 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Make Static
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Right-click quick-format + insert-variable menu */}
      {variableMenuOpen && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={closeVariableMenu} onContextMenu={(e) => { e.preventDefault(); closeVariableMenu() }} />
          <div
            className="fixed z-[80] w-72 rounded-md border border-gray-200 bg-white p-2.5 shadow-lg"
            style={{ left: variableMenuX, top: variableMenuY }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex flex-wrap items-center gap-1 border-b border-gray-100 pb-2">
              <ToolbarButton title="Bold" onClick={() => format('bold')}>
                <BoldIcon className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton title="Italic" onClick={() => format('italic')}>
                <ItalicIcon className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton title="Underline" onClick={() => format('underline')}>
                <span className="underline">U</span>
              </ToolbarButton>
              <ToolbarButton title="Decrease font size" onClick={decreaseFontSize}>
                A-
              </ToolbarButton>
              <ToolbarButton title="Increase font size" onClick={increaseFontSize}>
                A+
              </ToolbarButton>
              <label className="flex h-7 w-7 cursor-pointer items-center justify-center rounded font-bold hover:bg-indigo-50" title="Text color">
                A
                <input
                  type="color"
                  defaultValue="#000000"
                  onMouseDown={(e) => e.stopPropagation()}
                  onInput={(e) => setTextColor(e.currentTarget.value)}
                  className="h-0 w-0 opacity-0"
                />
              </label>
              {savedTableRef.current && (
                <>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={deleteTableFromMenu}
                    className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Delete Table
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={unwrapTableFromMenu}
                    className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Unwrap Table
                  </button>
                </>
              )}
            </div>
            <div className="mb-1 text-xs text-gray-500">Insert Variable</div>
            <input
              type="text"
              autoFocus
              className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={variableMenuSearch}
              onChange={(e) => setVariableMenuSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitCustomVariableFromMenu()}
              placeholder="Search or type a custom field.path..."
            />
            <div className="max-h-64 overflow-y-auto border-t border-gray-100">
              {variableMenuOptions.length === 0 ? (
                <div className="p-2 text-xs text-gray-500">No matches - press Enter to insert &quot;{variableMenuSearch}&quot; as a custom field path.</div>
              ) : (
                variableMenuOptions.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => chooseVariableFromMenu(f.path)}
                    className="block w-full border-b border-gray-50 px-1 py-1.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                  >
                    {f.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Toolbar "Repeat" menu */}
      {repeatMenuOpen && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={closeRepeatMenu} />
          <div
            className="fixed z-[80] w-72 rounded-md border border-gray-200 bg-white p-2.5 shadow-lg"
            style={{ left: repeatMenuX, top: repeatMenuY }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 text-xs text-gray-500">Insert a repeating section for which list?</div>
            <div className="max-h-64 overflow-y-auto border-t border-gray-100">
              {repeatMenuOptions.length === 0 ? (
                <div className="p-2 text-xs text-gray-500">
                  No list-shaped fields found here - click elsewhere in the document (outside any repeating section), or expand a
                  repeating section's item in the Variables panel first.
                </div>
              ) : (
                repeatMenuOptions.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => chooseRepeatFromMenu(f.path)}
                    className="block w-full border-b border-gray-50 px-1 py-1.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                  >
                    {f.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Toolbar "Table" menu */}
      {tableMenuOpen && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={closeTableMenu} />
          <div
            className="fixed z-[80] w-56 rounded-md border border-gray-200 bg-white p-1 shadow-lg"
            style={{ left: tableMenuX, top: tableMenuY }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => insertTable()}
              className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50"
            >
              <TableCellsIcon className="h-4 w-4" />
              Insert 2x2 Table
            </button>
            {tableMenuHasCellContext ? (
              <>
                <div className="my-1 h-px bg-gray-100" />
                <button type="button" onClick={() => insertTableRow('above')} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  Insert Row Above
                </button>
                <button type="button" onClick={() => insertTableRow('below')} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  Insert Row Below
                </button>
                <button type="button" onClick={() => insertTableColumn('left')} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  Insert Column Left
                </button>
                <button type="button" onClick={() => insertTableColumn('right')} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  Insert Column Right
                </button>
                <div className="my-1 h-px bg-gray-100" />
                <button type="button" onClick={deleteTableRow} className="block w-full rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50">
                  Delete Row
                </button>
                <button type="button" onClick={deleteTableColumn} className="block w-full rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50">
                  Delete Column
                </button>
                <div className="my-1 h-px bg-gray-100" />
                <button type="button" onClick={() => toggleTableBorders(true)} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  All Borders
                </button>
                <button type="button" onClick={() => toggleTableBorders(false)} className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-indigo-50">
                  No Borders
                </button>
                <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-indigo-50">
                  <input
                    type="color"
                    defaultValue="#FFF3CD"
                    onChange={(e) => setTableCellBackground(e.target.value)}
                    className="h-5 w-6 cursor-pointer border-0 p-0"
                  />
                  Cell Shading
                </label>
              </>
            ) : (
              <div className="p-2 text-xs text-gray-500">Click inside an existing table cell first to see row/column/shading options here.</div>
            )}
          </div>
        </>
      )}

      {/* Test with JSON modal */}
      {testJsonModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-6" onClick={() => setTestJsonModalOpen(false)}>
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <span className="text-sm font-semibold">Test with a Resume Analysis JSON Payload</span>
              <button type="button" onClick={() => setTestJsonModalOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="mb-2 text-sm text-gray-500">
                Paste a resume_analysis JSON payload below (the same shape produced by the resume-analysis pipeline). The template
                will be rendered against it and downloaded as a .docx - no file upload or AI analysis needed.
              </p>
              <textarea
                className="w-full rounded-md border border-gray-300 p-2 font-mono text-xs"
                rows={14}
                value={testJsonDraft}
                onChange={(e) => setTestJsonDraft(e.target.value)}
                placeholder='{"personal_info": {"full_name": "Jane Doe", ...}, "career_analysis": {...}, ...}'
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={testMutation.isPending}
                  onClick={() => setTestJsonModalOpen(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={testMutation.isPending}
                  onClick={submitTestJson}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {testMutation.isPending ? 'Formatting...' : 'Format & Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help modal */}
      {helpOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-6" onClick={() => setHelpOpen(false)}>
          <div className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <span className="text-sm font-semibold">Document Map Editor - Usage Guide</span>
              <button type="button" onClick={() => setHelpOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 overflow-y-auto p-5 text-sm text-gray-600">
              <p>
                The document is a live, editable page - click anywhere in ordinary (non-green) text and type, just like a word
                processor.
              </p>
              <p>
                <b>Binding a variable:</b> drag any field from the Variables panel and drop it where you want it in the document,
                or right-click to open a menu with quick formatting plus a searchable "Insert Variable" list.
              </p>
              <p>
                <b>Reassigning/removing a binding:</b> bound content is highlighted green and always shows its literal
                placeholder tag, never a real value. Click it to reassign it from the dropdown, type any custom field path, or
                convert it to permanent static text.
              </p>
              <p>
                <b>Repeating sections:</b> a dashed blue box labeled "Repeats &times;N" represents a block that repeats once per
                item in the real list it's bound to. Drag an array's "+ Repeat Section" chip from the Variables panel to insert a
                new one, or use the toolbar's "Repeat" button.
              </p>
              <p>
                <b>Tables:</b> use the toolbar's "Table" dropdown to insert a table or, with the cursor in an existing cell, to
                add/remove rows and columns, toggle borders, or shade a cell. Right-click while inside a table for Delete
                Table/Unwrap Table.
              </p>
              <p>
                <b>Testing:</b> click Preview to render the current document against this template's example data, or Test with
                Resume JSON to render it against a payload you paste in.
              </p>
              <p>
                <b>Saving:</b> Save as Active persists your edits and makes this template live. Save as Draft persists them without
                going live.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentMapEditor
